'use client'

import {useEffect, useState} from "react";
import Image from "next/image";
import classes from "./page.module.scss";
import MediatorCard from "@/app/components/mediator-card/MediatorCard";
import Arrow from "@/app/assets/images/icons/arrowLeft.svg";
import Input from "@/app/components/form-fields/input/Input";
import Select from "@/app/components/form-fields/select/Select";
import Link from "next/link";
import {countryService} from "@/app/services/CountryService";
import {useTranslation} from "react-i18next";
import Wrapper from "@/app/components/wrappers/Wrapper";
import {useAppContext} from "@/app/context/UserContext";
import {Form, Spin} from "antd";
import {debounce} from "lodash";
import NoResultsImg from "@/app/assets/images/mediators/noResults.svg";
import {LoadingOutlined} from "@ant-design/icons";
import Footer from "@/app/components/footer/Footer";

const Search = ({searchParams}) => {
    const {t} = useTranslation();
    const [loading, setLoading] = useState(true);
    const [mediators, setMediators] = useState([]);
    const {user} = useAppContext();
    const [formData, setFormData] = useState({
        name: searchParams.name || '',
        company: searchParams.company || '',
        country_id: searchParams.country_id || '',
        country_label: searchParams.country_label || '',
        industry: searchParams.industry || '',
        position: searchParams.position || ''
    });
    const [error, setError] = useState(null);
    const [countries, setCountries] = useState([]);
    const [query, setQuery] = useState("");
    const countryOptions = countries.map(country => ({
        value: country.id, label: country.name,
    }));

    const handleChange = debounce((field, value) => {
        setFormData(prev => ({...prev, [field]: value}));
    }, 500);

    const handleSelectChange = (field, selectedValue) => {
        setFormData(prev => ({
            ...prev,
            [field]: selectedValue ? selectedValue.value : '',
            country_label: selectedValue ? selectedValue.label : ''
        }));
        if (!selectedValue) {
            setQuery('');
        }
    };

    useEffect(() => {
        const fetchMediators = async () => {
            setLoading(true)
            let queryParams = {
                ...(formData.name && {name: formData.name}), ...(formData.company && {company: formData.company}), ...(formData.industry && {industry: formData.industry}), ...(formData.position && {position: formData.position}), ...(formData.country_id && formData.country_id !== "null" && {country_id: formData.country_id}),
            };

            const queryString = new URLSearchParams(queryParams).toString();
            try {
                const res = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/search?${queryString}`);
                const data = await res.json();
                setMediators(data.data);
                setLoading(false);
            } catch (err) {
                setError(err);
                setLoading(false);
            }
        };

        fetchMediators();
    }, [formData]);

    useEffect(() => {
        countryService.getAll(query).then(setCountries).catch(setError);
    }, [query]);

    return (<Wrapper className={classes.bgColor}>
            <div className={classes.container}>
                <div className={classes.searchResults}>
                    <div className={classes.results}>
                        <Link href="/" passHref>
                            <Image src={Arrow} alt="arrow"/>
                        </Link>
                        <h1 className={classes.searchTitle}>{t('searchPage.bestMatched')}</h1>
                        <span>{mediators.length} {t('searchPage.searchesFor')} {formData.name ? `"${formData.name}"` : ''}</span>
                    </div>
                    <Form initialValues={formData} className={classes.rowSearch}>
                        <div className={classes.inputGroup}>
                            <Form.Item name={'name'}>
                                <Input
                                    className={classes.borderTwo}
                                    // value={formData.name}
                                    placeholder={t('placeholders.nameAndSurname')}
                                    onChange={e => handleChange('name', e.target.value)}
                                />
                            </Form.Item>
                            <Form.Item name={'company'}>
                                <Input
                                    className={classes.company}
                                    placeholder={t('placeholders.company')}
                                    onChange={e => handleChange('company', e.target.value)}
                                />
                            </Form.Item>
                            <Form.Item>
                                <Select
                                    className={classes.customSelect}
                                    placeholder={t('placeholders.country')}
                                    onChange={handleSelectChange.bind(null, 'country_id')}
                                    filterOption={() => true}
                                    onSearch={(e) => setQuery(e)}
                                    options={countryOptions}
                                    allowClear
                                    labelInValue
                                    value={formData.country_id && formData.country_id !== "null" ? {
                                        value: formData.country_id, label: formData.country_label
                                    } : undefined}/>
                            </Form.Item>
                        </div>
                        <div className={`${classes.inputGroup} ${classes.border}`}>
                            <div className={classes.staticText}>
                                <span>{t('searchPage.advancedSearch')}</span>
                            </div>
                            <Form.Item name={'position'}>
                                <Input
                                    placeholder="Position"
                                    onChange={e => handleChange('position', e.target.value)}
                                />
                            </Form.Item>
                            <Form.Item name={'industry'}>
                                <Input
                                    className={classes.borderOne}
                                    placeholder="Industry"
                                    onChange={e => handleChange('industry', e.target.value)}
                                />
                            </Form.Item>
                        </div>
                    </Form>
                </div>
                <div className={classes.mediators}>
                    {mediators.length > 0 ? (mediators.map((mediator, index) => (
                        <MediatorCard key={index} item={mediator}/>))) : (<div className={classes.noResults}>
                        {!loading ? <>
                            <Image className={classes.noResultsImg} src={NoResultsImg} alt={'no results image'}/>
                            <h3>{t('searchPage.noResults')}</h3>
                            <p>{t('searchPage.unsuccessfulSearch')}</p>
                        </> : <Spin indicator={<LoadingOutlined style={{fontSize: 55}} spin/>} spinning={true}/>}
                    </div>)}
                </div>
            </div>
        </Wrapper>);
};

export default Search;